import { IErrorLogger, IBreadcrumbLogger } from '@verv-inc/js-toolkit';
import {
	IVervAPIServices,
	APIError,
	Status,
	API_ERROR_TYPE,
	BiDataRequest,
	IUserData,
} from './VervAPITypes';
import showAlert from './../../showAlert';

export const ComposeVervAPIServices = (
	apiURL = 'https://api-test.verv.com',
	errorLogger: IErrorLogger & IBreadcrumbLogger,
): IVervAPIServices => {
	return {
		async getPaypalConfig() {
			const response = await fetch(`${apiURL}/v4/pp/config?group_bundle=verv`);
			const data = await response.json();
			if (data.status === Status.success) return data;

			errorLogger.logError({
				name: 'pp/config',
				message: '',
			});

			return data;
		},

		async ping() {
			const response = await fetch(`${apiURL}/v4/ping`);

			const data = await response.json();
			if (data.status === Status.success) return Status.success;

			errorLogger.logError({
				name: 'ping',
				message: data.err[0].msg || 'api is unavailable',
			});

			return Status.failure;
		},

		async registerPaypalSubscription(dataToSend) {
			const response = await fetch(`${apiURL}/v4/pp/register-subscription`, {
				method: 'POST',
				body: JSON.stringify(dataToSend),
			});
			const data = await response.json();
			if (data.status === Status.success) return data;

			const apiError: APIError = new APIError(data.err[0].msg, data.err[0].type);

			errorLogger.logError({
				name: 'pp/register-subscription',
				message: apiError.message,
			});

			return {
				status: Status.failure,
				error: apiError,
			};
		},

		async createPaypalOrder(dataToSend) {
			const response = await fetch(`${apiURL}/v4/pp/create-order`, {
				method: 'POST',
				body: JSON.stringify(dataToSend),
			});
			const data = await response.json();
			if (data.status === Status.success) return data;

			const apiError: APIError = new APIError(data.err[0].msg, data.err[0].type);

			errorLogger.logError({
				name: 'pp/create-order',
				message: apiError.message,
			});

			return {
				status: Status.failure,
				error: apiError,
			};
		},

		async registerPaypalOrder(order) {
			const response = await fetch(`${apiURL}/v4/pp/orders/${order.orderID}/capture`, {
				method: 'POST',
			});
			const data = await response.json();
			if (data.status === Status.success) return data;

			const apiError: APIError = new APIError(data.err[0].msg, data.err[0].type);

			errorLogger.logError({
				name: 'pp/capture',
				message: apiError.message,
			});

			return {
				status: Status.failure,
				error: apiError,
			};
		},

		async getStripeConfig() {
			const response = await fetch(`${apiURL}/v4/st/config`, {
				method: 'GET',
			});
			const data = await response.json();
			if (data.status === Status.success) return data;

			errorLogger.logError({
				name: 'st/config',
				message: '',
			});

			return data;
		},

		async createStripeSubscription(dataToSend) {
			const response = await fetch(`${apiURL}/v4/st/create-subscription`, {
				method: 'POST',
				body: JSON.stringify(dataToSend),
			});
			const data = await response.json();

			if (data.status === Status.success) return data;

			const apiError: APIError = new APIError(data.err[0].msg, data.err[0].type);

			// mark insufficient funds and declined errors as breadcrumbs
			if (Object.values(API_ERROR_TYPE).includes(apiError.type as API_ERROR_TYPE)) {
				errorLogger.addBreadcrumb('st/create-subscription', apiError.message);
			} else {
				errorLogger.logError(
					{
						name: 'st/create-subscription',
						message: apiError.message,
					},
					{
						type: apiError.type,
					},
				);
			}

			return {
				status: Status.failure,
				error: apiError,
			};
		},

		async changeStripeSubscription(dataToSend) {
			const response = await fetch(`${apiURL}/v4/st/change-subscription`, {
				method: 'POST',
				body: JSON.stringify(dataToSend),
			});
			const data = await response.json();

			if (data.status === Status.success) return data;

			const apiError: APIError = new APIError(data.err[0].msg, data.err[0].type);

			// mark insufficient funds and declined errors as breadcrumbs
			if (Object.values(API_ERROR_TYPE).includes(apiError.type as API_ERROR_TYPE)) {
				errorLogger.addBreadcrumb('st/create-subscription', apiError.message);
			} else {
				errorLogger.logError(
					{
						name: 'st/create-subscription',
						message: apiError.message,
					},
					{
						type: apiError.type,
					},
				);
			}

			return {
				status: Status.failure,
				error: apiError,
			};
		},

		async intentStripePayment(paymentIntentId) {
			const response = await fetch(`${apiURL}/v4/st/confirm?payment_intent=${paymentIntentId}`, {
				method: 'GET',
			});
			const data = await response.json();

			if (data.status === Status.success) return data;

			const apiError: APIError = new APIError(data.err[0].msg, data.err[0].type);

			errorLogger.logError(
				{
					name: 'st/confirm?payment_intent',
					message: apiError.message,
				},
				{
					type: apiError.type,
				},
			);

			return {
				status: Status.failure,
				error: apiError,
			};
		},

		async getWebCatalogProduct(productName) {
			// const response = await fetch(`${apiURL}/v4/web_catalog?product=${productName}`);
			// const data = await response.json();
			// if (data.status === Status.success) return data;
			// errorLogger.logError({
			// 	name: '/v4/web_catalog?product',
			// 	message: `Product not found. ${productName}`,
			// });
			// return data;

			try {
				const response = await fetch(`${apiURL}/v4/web_catalog?product=${productName}`);
				if (!response.ok) {
					showAlert();
				}
				const data = await response.json();
				if (data.status === Status.success) return data;
			} catch (error) {
				showAlert();
			}
		},

		async sendBiData(requestBody: BiDataRequest) {
			const breadcrumb: string =
				requestBody.extra_data.bi_data != null
					? JSON.stringify(requestBody.extra_data.bi_data)
					: 'no bi_data';
			errorLogger.addBreadcrumb('v4/web_extra_data', breadcrumb);

			const response = await fetch(`${apiURL}/v4/web_extra_data`, {
				method: 'POST',
				body: JSON.stringify({ ...requestBody }),
			});
			const data = await response.json();

			if (data.status === Status.success) return data;

			const apiError: APIError = new APIError(data.err[0].msg);

			errorLogger.logError({
				name: 'v4/web_extra_data',
				message: apiError.message,
			});

			return {
				status: Status.failure,
				error: apiError,
			};
		},

		async sendUserData(requestBody: IUserData) {
			const response = await fetch(`${apiURL}/v4/web_extra_data`, {
				method: 'POST',
				body: JSON.stringify({ ...requestBody }),
			});
			const data = await response.json();

			if (data.status === Status.success) return data;

			const apiError: APIError = new APIError(data.err[0].msg);

			errorLogger.logError({
				name: 'userData: v4/web_extra_data',
				message: apiError.message,
			});

			return {
				status: Status.failure,
				error: apiError,
			};
		},

		async purchaseProduct(dataToSend) {
			const response = await fetch(`${apiURL}/v4/st/purchase-product`, {
				method: 'POST',
				body: JSON.stringify(dataToSend),
			});
			const data = await response.json();
			if (data.status === Status.success) return data;

			const apiError: APIError = new APIError(data.err[0].msg, data.err[0].type);

			errorLogger.logError({
				name: 'st/purchase-product',
				message: apiError.message,
			});

			return {
				status: Status.failure,
				error: apiError,
			};
		},
	};
};
